import { FC, ReactNode, useEffect, useState } from 'react';
import { Flex, Button, BoxV2 as Box, TextInput } from 'portal-commons';
import { useForm, useController } from 'react-hook-form';

import Modal from '@/components/ModalV2';
import { useTextInputSuggestions } from '@/hooks';
import { isRequired } from '@/applications/CspPortal/Campaigns/validators';
import { getRcsFallbackCampaignSuggestions } from '@/apis/services';
import { UseCase } from '@/apis/core';

export enum ModalType {
  Remove = 'REMOVE',
  Update = 'UPDATE',
  Select = 'SELECT',
}

interface TenDlcCampaignFallbackEditModalProps {
  type: ModalType | null;
  open: boolean;
  rcsCampaignUid: string;
  fallbackCampaignUid?: string;
  brandUid: string;
  tenDlcUseCases: UseCase[];
  onClose: () => void;
  loading?: boolean;
  onUpdate?: (fallbackCampaignUid: string) => void;
  onSelect?: (fallbackCampaignUid: string) => void;
  onRemove?: () => void;
}

const TenDlcCampaignFallbackEditModal: FC<
  TenDlcCampaignFallbackEditModalProps
> = ({
  type,
  open,
  rcsCampaignUid,
  fallbackCampaignUid,
  brandUid,
  tenDlcUseCases,
  loading,
  onClose,
  onUpdate,
  onSelect,
  onRemove,
}) => {
  const { control, reset, handleSubmit } = useForm({
    mode: 'onSubmit',
  });
  const [fallbackCampaignInput, setFallbackCampaignInput] = useState('');

  const {
    field: {
      value: newFallbackCampaignUid,
      onChange: setNewFallbackCampaignUid,
    },
    fieldState: { error: newFallbackCampaignUidError },
  } = useController({
    name: 'fallbackCampaignUid',
    control,
    defaultValue: '',
    disabled: type === ModalType.Remove,
    rules: {
      validate: {
        isRequired,
      },
    },
  });

  const fallbackCampaignSuggestions = useTextInputSuggestions({
    input: fallbackCampaignInput,
    threshold: 3,
    fetchSuggestions: (input: string) =>
      getRcsFallbackCampaignSuggestions(
        {
          campaignUidPrefix: input,
          brandUid,
        },
        tenDlcUseCases
      ),
  });

  useEffect(() => {
    if (!open) {
      reset({
        fallbackCampaignUid: '',
      });
      setFallbackCampaignInput('');
    }
  }, [open]);

  const getDialogTitle = () => {
    switch (type) {
      case ModalType.Remove:
        return 'Remove 10DLC Fallback Campaign';
      case ModalType.Update:
        return 'Update 10DLC Fallback Campaign';
      case ModalType.Select:
        return 'Select 10DLC Fallback Campaign';
      default:
        return '';
    }
  };

  const handleConfirmClick = (data: Record<string, string>) => {
    switch (type) {
      case ModalType.Remove:
        onRemove?.();
        break;
      case ModalType.Update:
        onUpdate?.(data.fallbackCampaignUid);
        break;
      case ModalType.Select:
        onSelect?.(data.fallbackCampaignUid);
        break;
      default:
        break;
    }
  };

  const Bold = ({ children }: { children: ReactNode }) => (
    <span style={{ fontWeight: 800 }}>{children}</span>
  );

  return (
    <Modal
      open={open}
      onClose={onClose}
      data-testid="10dlcFallbackEditModal"
      sx={{ width: '525px' }}
    >
      <Flex
        sx={{
          justifyContent: 'center',
          flexDirection: 'column',
          color: 't.black80',
          fontSize: 'H300',
          width: '100%',
          lineHeight: 'normal',
        }}
      >
        <Box
          role="heading"
          sx={{
            fontSize: 'H600',
            fontWeight: 'semiBold',
            mb: '27px',
            textAlign: 'center',
          }}
        >
          {getDialogTitle()}
        </Box>
        <form noValidate onSubmit={handleSubmit(handleConfirmClick)}>
          <div style={{ paddingTop: '11px', paddingBottom: '11px' }}>
            {type === ModalType.Remove && (
              <>
                To remove <Bold>{fallbackCampaignUid}</Bold> as{' '}
                <Bold>{rcsCampaignUid}</Bold>’s fallback campaign, confirm below
              </>
            )}
            {type === ModalType.Select && (
              <>
                Select a 10DLC Campaign to function as a fallback messaging
                system if the primary RCS fails.
              </>
            )}
            {type === ModalType.Update && (
              <>
                To update your 10DLC fallback campaign select a new campaign
                below.
              </>
            )}
          </div>
          {type && [ModalType.Select, ModalType.Update].includes(type) && (
            <Box sx={{ py: 'xs' }}>
              <TextInput
                label="10DLC Campaign"
                placeholder="Type Campaign ID to search"
                suggestions={fallbackCampaignSuggestions}
                onChange={setFallbackCampaignInput}
                onSelect={setNewFallbackCampaignUid}
                value={fallbackCampaignInput}
                error={!!newFallbackCampaignUidError?.message}
                note={newFallbackCampaignUidError?.message}
                suggestionThreshold={3}
              />
            </Box>
          )}
          <Flex
            sx={{
              columnGap: '18px',
              padding: '30px 0',
              pb: '40px',
              flexDirection: 'row',
              justifyContent: 'center',
              gap: 'l',
            }}
          >
            <Button
              variant="outline"
              onClick={onClose}
              data-testid="10dlcFallbackEditModalCancelButton"
            >
              Cancel
            </Button>
            <Button
              type="submit"
              disabled={
                !!loading ||
                (!newFallbackCampaignUid &&
                  [ModalType.Select, ModalType.Update].includes(type))
              }
              data-testid="10dlcFallbackEditModalConfirmButton"
            >
              {type === ModalType.Update ? 'Update' : 'Confirm'}
            </Button>
          </Flex>
        </form>
      </Flex>
    </Modal>
  );
};

export default TenDlcCampaignFallbackEditModal;
